import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Link } from 'react-router';
import { Trash2 } from 'lucide-react';
import { BundleQuantityEditor } from '~/components/common/BundleQuantityEditor';
import { SectionPanel } from '~/components/ui/SectionPanel';
import { CellEditPopover } from '~/components/ui/table/CellEditPopover';
import {
  useAddProductToBuyList,
  useRemoveProductFromBuyList,
} from '~/api/buyList';
import { formatQuantityCell, toastGraphQLError } from '~/utils';
import { wholeBundlePieces } from '~/components/ui/bundleQuantity';
import {
  buyListItemChange,
  type BuyListItemChange,
} from '~/pages/buy-lists/lib/buyListItemChange';
import type { InitialProductLengthPick } from '~/components/common/productLengthChoice';
import { BuyListAddProductRow } from './BuyListAddProductRow';
import { BuyListItemMarketPrice } from './BuyListItemMarketPrice';
import { ItemsInSellerQuotesToggle } from './ItemsInSellerQuotesToggle';
import {
  NO_LENGTH_IDS,
  useItemsInSellerQuotesFold,
} from './useItemsInSellerQuotesFold';
import { ProductLengthGrid } from '~/components/common/ProductLengthGrid';
import { BuyListTalliesBlock } from './tallies/BuyListTalliesBlock';
import type { BuyListDetailTally } from './tallies/types';
import type { BuyListDetailItem } from './types';

type BuyListItemsSectionProps = {
  buyListId: string;
  transportVehicleId: string;
  items: BuyListDetailItem[];
  tallies: BuyListDetailTally[];
  canManageItems: boolean;
  platformProductLengthIdsInSellerQuotes?: ReadonlySet<string>;
  summary?: ReactNode;
  viewerId: string | null;
  initialPick?: InitialProductLengthPick;
  onSavingChange?: (isSaving: boolean) => void;
  onPendingPickChange?: (hasPendingPick: boolean) => void;
};

const IGNORE_CHANGE = () => {};

export function BuyListItemsSection({
  buyListId,
  transportVehicleId,
  items,
  tallies,
  canManageItems,
  platformProductLengthIdsInSellerQuotes = NO_LENGTH_IDS,
  summary,
  viewerId,
  initialPick,
  onSavingChange = IGNORE_CHANGE,
  onPendingPickChange = IGNORE_CHANGE,
}: Readonly<BuyListItemsSectionProps>) {
  const { t } = useTranslation('inventory');
  const itemsTableId = useId();
  const {
    itemsInSellerQuotes,
    listedItems,
    showsItemsInSellerQuotes,
    platformProductLengthIdsHiddenInSellerQuotes,
    toggleItemsInSellerQuotes,
  } = useItemsInSellerQuotesFold(items, platformProductLengthIdsInSellerQuotes);

  const [
    removingPlatformProductsLengthId,
    setRemovingPlatformProductsLengthId,
  ] = useState<string | null>(null);

  // One shared mutation serves every row, so its pending flag cannot say which
  // row is saving.
  const [savingPlatformProductsLengthId, setSavingPlatformProductsLengthId] =
    useState<string | null>(null);

  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [isSavingTally, setIsSavingTally] = useState(false);
  const isSaving =
    isAddingProduct || isSavingTally || savingPlatformProductsLengthId !== null;
  useEffect(() => onSavingChange(isSaving), [isSaving, onSavingChange]);

  const { mutate: addProductToBuyList } = useAddProductToBuyList();
  const { mutate: removeProductFromBuyList } = useRemoveProductFromBuyList();

  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  const saveQuantity = (
    platformProductsLengthId: string,
    change: BuyListItemChange,
    onDone: () => void
  ) => {
    setSavingPlatformProductsLengthId(platformProductsLengthId);
    addProductToBuyList(
      { buyListId, platformProductsLengthId, ...change },
      {
        onSuccess: () => {
          toast.success(t('inventory.buy_list.quantity_success'));
          onDone();
        },
        onError: toastGraphQLError,
        onSettled: () => setSavingPlatformProductsLengthId(null),
      }
    );
  };

  const removeProduct = (platformProductsLengthId: string) => {
    setRemovingPlatformProductsLengthId(platformProductsLengthId);
    removeProductFromBuyList(
      { buyListId, platformProductsLengthId },
      {
        onSuccess: () => toast.success(t('inventory.buy_list.remove_success')),
        onError: toastGraphQLError,
        onSettled: () => setRemovingPlatformProductsLengthId(null),
      }
    );
  };

  const closeEditorFor = (itemId: string) =>
    setEditingItemId((current) => (current === itemId ? null : current));

  const renderActions = (item: BuyListDetailItem, rowIndex: number) => {
    const platformProductsLengthId = item.platformProductsLength.id;
    const isSaving =
      platformProductsLengthId === savingPlatformProductsLengthId;
    const onDone = () => closeEditorFor(item.id);
    const savedOverride = item.piecesPerBundleOverride ?? undefined;
    const removeLabel = t('inventory.buy_list.remove_item', {
      product: item.platformProductsLength.platformProduct.name,
    });

    return (
      <>
        <CellEditPopover
          isOpen={editingItemId === item.id}
          onOpen={() => setEditingItemId(item.id)}
          onDone={onDone}
          isBusy={isSaving}
          columnLabel={t('inventory.buy_list.quantity')}
          testId={`edit-quantity-${rowIndex}`}
          anchorClassName="text-neut-600"
          renderEditor={() => (
            <BundleQuantityEditor
              quantity={item.quantity}
              platformProductsLength={item.platformProductsLength}
              piecesPerBundle={savedOverride}
              isPiecesPerBundleClearable
              quantityToSave={(quantity, piecesPerBundle) =>
                wholeBundlePieces(
                  quantity,
                  piecesPerBundle ??
                    item.platformProductsLength.platformProduct.piecesPerBundle
                      .value,
                  item.platformProductsLength
                )
              }
              isSaving={isSaving}
              testId="buy-list-detail"
              onSave={(quantity, piecesPerBundle) =>
                saveQuantity(
                  platformProductsLengthId,
                  buyListItemChange(quantity, piecesPerBundle, savedOverride),
                  onDone
                )
              }
              onCancel={onDone}
            />
          )}
        />
        <span data-testid={`cell-actions-${rowIndex}`}>
          <button
            type="button"
            className="text-neut-600 hover:text-error-700 cursor-pointer disabled:opacity-50"
            aria-label={removeLabel}
            title={removeLabel}
            data-testid={`buy-list-detail-delete-${platformProductsLengthId}`}
            disabled={
              removingPlatformProductsLengthId === platformProductsLengthId
            }
            onClick={() => removeProduct(platformProductsLengthId)}
          >
            <Trash2 size={14} aria-hidden="true" />
          </button>
        </span>
      </>
    );
  };

  return (
    <section data-testid="buy-list-detail-items-section">
      <SectionPanel bodyClassName="">
        {summary && (
          <div className="border-b border-neut-border">{summary}</div>
        )}
        {canManageItems && (
          <BuyListAddProductRow
            buyListId={buyListId}
            transportVehicleId={transportVehicleId}
            items={items}
            tallies={tallies}
            platformProductLengthIdsHiddenInSellerQuotes={
              platformProductLengthIdsHiddenInSellerQuotes
            }
            initialPick={initialPick}
            onSavingChange={setIsAddingProduct}
            onPendingPickChange={onPendingPickChange}
          />
        )}
        <div className="p-4" data-testid="buy-list-detail-table">
          {items.length === 0 && (
            <p className="text-sm text-neut-sec">
              {t('inventory.buy_list.items_empty')}
            </p>
          )}
          {listedItems.length > 0 && (
            <ProductLengthGrid
              id={itemsTableId}
              items={listedItems}
              testId="buy-list-detail-items"
              renderPrice={
                listedItems.some(
                  (item) => item.platformProductsLength.latestPriceMid
                )
                  ? (item) => <BuyListItemMarketPrice item={item} />
                  : undefined
              }
              priceLabel={t('inventory.buy_list.market_price')}
              renderActions={canManageItems ? renderActions : undefined}
              renderCarts={(item) => (
                <ItemCarts carts={item.carts} viewerId={viewerId} />
              )}
            />
          )}
          {itemsInSellerQuotes.length > 0 && (
            <ItemsInSellerQuotesToggle
              count={itemsInSellerQuotes.length}
              isShown={showsItemsInSellerQuotes}
              followsListedItems={listedItems.length > 0}
              itemsTableId={itemsTableId}
              onToggle={toggleItemsInSellerQuotes}
            />
          )}
        </div>
        <BuyListTalliesBlock
          buyListId={buyListId}
          tallies={tallies}
          canManage={canManageItems}
          onSavingChange={setIsSavingTally}
        />
      </SectionPanel>
    </section>
  );
}

function ItemCarts({
  carts,
  viewerId,
}: Readonly<{ carts: BuyListDetailItem['carts']; viewerId: string | null }>) {
  const { t } = useTranslation('inventory');
  if (carts.length === 0) {
    return (
      <span className="text-neut-sec">
        {t('inventory.buy_list.not_in_any_cart')}
      </span>
    );
  }
  return (
    <ul className="flex flex-col gap-0.5">
      {carts.map(({ buyer, quantity }) => {
        const formattedQuantity = formatQuantityCell(
          quantity.value,
          quantity.unit
        );
        return (
          <li key={buyer.id}>
            {buyer.id === viewerId ? (
              <Link to="/cart" className="text-prim hover:text-prim-hover">
                {t('inventory.buy_list.in_carts_you', {
                  quantity: formattedQuantity,
                })}
              </Link>
            ) : (
              t('inventory.buy_list.in_carts_buyer', {
                name: `${buyer.firstName} ${buyer.lastName}`,
                quantity: formattedQuantity,
              })
            )}
          </li>
        );
      })}
    </ul>
  );
}
